import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';

export interface DropdownOption {
  value: any;
  displayName: string;
}

export interface SelectionChangeEvent {
  value: any;
  option: DropdownOption | null;
}

@Component({
  selector: 'single-select-dropdown-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectDropdownListComponent),
      multi: true
    }
  ],
  templateUrl: './single-select-dropdown-list.component.html',
  styleUrls: []
})
export class SingleSelectDropdownListComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() set options(value: DropdownOption[]) { this._options.set(value); }
  get options() { return this._options(); }
  private readonly _options = signal<DropdownOption[]>([]);

  @Input() label: string = '';
  @Input() helpText: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() showEmptyOption: boolean = false;
  @Input() emptyOptionText: string = '-- Select --';

  @Output() selectionChange = new EventEmitter<SelectionChangeEvent>();

  inputControl = new FormControl();
  private readonly filterValue = signal<string>('');
  
  private readonly destroy$ = new Subject<void>();
  private onChange = (value: any) => {};
  private onTouched = () => {};
  private currentValue: any = null;

  // Computed signal for all options including empty option
  allOptions = computed<DropdownOption[]>(() => {
    const options = this._options();
    const emptyOption: DropdownOption = { value: null, displayName: this.emptyOptionText };
    return this.showEmptyOption ? [emptyOption, ...options] : options;
  });

  // Computed signal for filtered options
  filteredOptions = computed<DropdownOption[]>(() => {
    const filter = this.filterValue();
    const options = this.allOptions();
    
    if (!filter || filter.trim() === '') {
      return options;
    }
    
    const filterValueLower = filter.toLowerCase().trim();
    return options.filter(option => 
      option.displayName.toLowerCase().includes(filterValueLower)
    );
  });

  constructor() {
    // Effect to handle when options change and we need to set a pending value
    effect(() => {
      const options = this.allOptions();
      if (this.currentValue !== null && options.length > 0) {
        const selectedOption = options.find(option => option.value === this.currentValue);
        if (selectedOption) {
          this.inputControl.setValue(selectedOption, { emitEvent: false });
        }
      }
    });
  }

  ngOnInit() {
    // Subscribe to input control changes for user selections
    this.inputControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (typeof value === 'object' && value !== null) {
        // User selected an option from dropdown
        this.onChange(value.value);
        this.selectionChange.emit({ value: value.value, option: value });
      } else if (typeof value === 'string') {
        // User is typing - update filter value
        this.filterValue.set(value);
        // Don't emit selectionChange for typing - only for actual selections
      }
      this.onTouched();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.currentValue = value;
    
    // Always try to find the matching option (including null values for empty option)
    const selectedOption = this.allOptions().find(option => option.value === value);
    this.inputControl.setValue(selectedOption || null, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  // Display function for autocomplete
  displayFn = (option: DropdownOption): string => {
    return option ? option.displayName : '';
  };

  get isDisabled(): boolean {
    return this.disabled || this.readonly;
  }

  get showError(): boolean {
    return this.required && this.inputControl.touched && !this.inputControl.value;
  }
}